import z from 'zod'
import { parseISODate, parseTimestamp } from '@/util/zod/validators'
import { MedSummaryReferenceSchema } from '@/medical-billing-summaries/references/store/schemas'
import { MedSummarySourceRef, MedSummarySourceRefSchema } from '@/medical-billing-summaries/sources/schemas'
import { FACILITY_NAME_MIN, FACILITY_NAME_MAX, PROCEDURE_CODE_MAX, COMMENT_MAX } from '../editable-field-config'

export enum BillingEntryType {
  MEDICAL_BILL = 'medical_bill',
  ADJUSTMENT = 'adjustment',
  PAYMENT = 'payment',
}

// A single entry field value paired with its reference id
export const EntryFieldItemSchema = <T extends z.ZodTypeAny>(valueSchema: T) =>
  z.object({
    reference_id: z.string().uuid().nullable(),
    value: valueSchema,
    updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  })

export type EntryFieldItem<T> = {
  reference_id: string | null
  value: T
  updated_at: number | null
}

// UI-ready entry field item
export type UIEntryFieldItem = {
  referenceId: string | null
  text: string | null
  updatedAt: number | null
}

// UI-ready medical billing summary entry
export type UIMedSummaryEntry = {
  id: string
  serviceDate: UIEntryFieldItem
  facilityName: UIEntryFieldItem
  entryType: BillingEntryType
  amount: UIEntryFieldItem
  procedureCode: UIEntryFieldItem
  filePath: string
  source: MedSummarySourceRef
  pages: number[]
  comment: string
  createdAt: number
  pdfOrder: number | null
}

export const MedSummaryEntrySchema = z.object({
  id: z.string().uuid(),
  service_date: EntryFieldItemSchema(z.preprocess((val, ctx) => (val === null ? null : parseISODate(val, ctx)), z.string().nullable())),
  facility_name: EntryFieldItemSchema(z.string().nullable()),
  entry_type: z.nativeEnum(BillingEntryType),
  amount: EntryFieldItemSchema(z.preprocess((val) => (val === null ? null : typeof val === 'string' ? parseFloat(val) : val), z.number().nullable())),
  procedure_code: EntryFieldItemSchema(z.string().nullable()),
  file_path: z.string(),
  source: MedSummarySourceRefSchema,
  pages: z.array(z.number().int().nonnegative()),
  comment: z.string().nullable(),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  pdf_order: z.number().int().nonnegative().nullable(),
})

export type MedSummaryEntry = z.infer<typeof MedSummaryEntrySchema>

export const MedSummaryEntriesResponseSchema = z.object({
  med_summary_id: z.string().uuid(),
  entries: z.array(MedSummaryEntrySchema),
  references: z.record(z.string().uuid(), MedSummaryReferenceSchema),
})

export type MedSummaryEntriesResponse = z.infer<typeof MedSummaryEntriesResponseSchema>

/**
 * Schema for updating a medical billing summary entry
 * - Omit field = keep existing value (no change)
 * - Send null = clear the field (only for nullable fields)
 * - Send value = update to new value
 */
export const MedSummaryEntryUpdateSchema = z.object({
  service_date: z.string().optional(), // YYYY-MM-DD ISO format
  facility_name: z.string().min(FACILITY_NAME_MIN).max(FACILITY_NAME_MAX).optional(),
  amount: z.number().nonnegative().optional(), // unsigned
  procedure_code: z.string().max(PROCEDURE_CODE_MAX).nullable().optional(),
  comment: z.string().max(COMMENT_MAX).nullable().optional(),
})

export type MedSummaryEntryUpdate = z.infer<typeof MedSummaryEntryUpdateSchema>
